import React from 'react';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  children,
  className,
  disabled,
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  // Use CSS variables for colors and border radius
  const variants = {
    primary: 'flex items-center px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-secondary)] transition-colors font-[\'Times_New_Roman\'] disabled:opacity-50 disabled:cursor-not-allowed',
    secondary: 'bg-[var(--color-surface)] text-[var(--color-on-surface)] hover:bg-[color:var(--color-background)] focus:ring-[var(--color-secondary)] disabled:bg-[color:var(--color-surface)]/50',
    outline: 'border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[color:var(--color-surface)] focus:ring-[var(--color-secondary)] disabled:border-[var(--color-border)] disabled:text-[var(--color-text-secondary)]',
    ghost: 'text-[var(--color-text)] hover:bg-[color:var(--color-surface)] focus:ring-[var(--color-secondary)] disabled:text-[var(--color-text-secondary)]',
    danger: 'bg-[var(--color-error)] text-[var(--color-on-error)] hover:bg-[color:var(--color-error)]/80 focus:ring-[var(--color-error)] disabled:bg-[var(--color-error)]/50'
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  return (
    <button
      className={[
        baseClasses,
        variants[variant],
        sizes[size],
        loading || disabled ? 'opacity-50 cursor-not-allowed' : '',
        className || ''
      ].join(' ')}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className={`animate-spin mr-2 ${iconSizes[size]} text-[var(--color-text)]`} />}
      {icon && !loading && <span className={`mr-2 ${iconSizes[size]}`}>{icon}</span>}
      {children}
    </button>
  );
};

export default Button;